/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {EntityTreeBranchModel} from '../../models/entity-tree-branch.model';
import {TreeNodeModel} from '../../../../settings/business-settings/models/tree-node-model';
import {EntityTreeModel} from '../../models/entity-tree.model';
import {EntityTypeEnum} from '../../../../settings/business-settings/configuration/v1/entity-type.enum';
import {EntityElementPathStateModel} from '../../entity-element/models/entity-element-path-state.model';
import {PathToModel} from '../../../../settings/business-settings/models/path-to.model';
import {EntityTreeBranchTypeEnum} from '../../models/entity-tree-branch-type.enum';
import {EntityTreeUtilService} from '../entity-tree-util.service';
import {EntityElementModel} from '../../entity-element/models/entity-element.model';
import {EntityTreeBranchDisplayDataModel} from '../../../../settings/business-settings/configuration/v1/models/entity-tree-branch-display-data.model';
import {BusinessSettingsDisplayDataModel} from '../../../../settings/business-settings/configuration/v1/models/business-settings-display-data.model';

@Injectable({
    providedIn: 'root'
})
export class EntityPathStateService {

    constructor(private entityUtil: EntityTreeUtilService) {
    }

    /**
     * Returns the path state for a given tree node
     * @param entity the entity that is represented by the tree node
     * @param rootTreeNode the relation tree to which the entity belongs
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding entity tree data
     * @param selectedBranch the selected specific branch from the entityTree.specificBranches
     */
    getTreeNodePathState(entity: EntityTreeBranchModel,
                         rootTreeNode: TreeNodeModel,
                         entityTree: EntityTreeModel,
                         entityTreeData: BusinessSettingsDisplayDataModel,
                         selectedBranch: EntityTreeBranchTypeEnum): EntityElementPathStateModel {
        // base initial state
        let state: EntityElementPathStateModel = this.getBaseTreeNodePathState(entity,
                rootTreeNode,
                entityTree,
                entityTreeData.entitiesData);

        const treeNode = this.entityUtil.getCurrentEntityTreeNode(entity, rootTreeNode, entityTree, entityTreeData.entitiesData);
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, entityTreeData.entitiesData);


        if (entity.skippable) {
            // if the entity is skippable and is in fact skipped, the state may be overridden
            if (this.entityIsSkipped(entity.name, treeNode, entity.isFinal,
                    this.entityUtil.enableEntity(entity, entityTreeData.entitiesData, entityTree))) {
                state = this.getSkippableEntityPathState(entity, rootTreeNode, entityTree, entityTreeData.entitiesData);

                // if the entity is skippable, is not skipped and the relation tree is null or empty, the state may be overridden
            } else if (treeNode.relationTree == null || treeNode.relationTree.length === 0) {
                state = {...state, unfinishedPath: {isOrigin: false, isInPath: true}};
            }
        }

        if (treeNode != null && treeNode.relationTree != null) {

            //  if the entity is in the common branch and one of children entities is in the specific branch, the state may be overridden
            if (this.entityIsInCommonBranch(entity, entityTree) && this.entityHasChildInSpecificBranch(entity, entityTree)) {
                state = this.getEntityWithChildrenInDifferentBranchPathState(entity, treeNode, entityTree, selectedBranch, state);
            }

            // if a child with represent parent, the state may be overridden
            if (this.hasChildWithRepresentParent(entity.pathTo, entityTree, entityTreeData.entitiesData)) {
                state = this.getEntityWithRepresentableChildPathState(entity, entityTree, entityTreeData.entitiesData, state);
            }

            // if entity is responsible to represent the parent state, the state may be overridden
            if (entityData.representParent) {
                state = this.getRepresentableChildParentState(entity, rootTreeNode, entityTree, entityTreeData.entitiesData, state);
            }

            // if the entity is the last of the common branch and specific branches exist
            if (this.entityUtil.isLastItemInBranch(entityTree.commonBranch, entityTreeData.entitiesOrder.commonBranch, entity.name)
                    && this.entityUtil.entityTreeHasSpecificBranches(entityTree)) {
                state = this.getEntityWithRepresentableChildPathState(entity, entityTree, entityTreeData.entitiesData, state);
            }
        }

        return state;
    }

    /**
     * Returns the path state for a given element
     * @param entity the entity to which the element belongs
     * @param element the element from which to return the path state
     * @param rootTreeNode the relation tree to which the entity and the element belong
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding entity tree data
     */
    getElementPathState(entity: EntityTreeBranchModel,
                        element: EntityElementModel,
                        rootTreeNode: TreeNodeModel,
                        entityTree: EntityTreeModel,
                        entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): EntityElementPathStateModel {
        let pathState: EntityElementPathStateModel = null;

        const node = this.entityUtil.getCurrentEntityTreeNode(entity, rootTreeNode, entityTree, entityTreeData);

        if (node != null) {
            const relationTreeNode = node.relationTree.find((entry) => entry.type === entity.name && entry.code === element.code);
            if (relationTreeNode != null) {
                pathState = relationTreeNode.pathState;
            }
        }

        return pathState;
    }

    treeTabHasInactivePath(rootTreeNode: TreeNodeModel,
                           entityTree: EntityTreeModel,
                           entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>,
                           selectedTab: EntityTreeBranchTypeEnum): boolean {
        let hasInactive = false;

        const selectedBranch = entityTree.specificBranches.get(selectedTab);

        // Get the 1st entity from the selected branch
        const specificBranchRootEntity: EntityTreeBranchModel = selectedBranch.values()
                .next().value;

        const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                specificBranchRootEntity, rootTreeNode, entityTree, entityTreeData);

        if (currentTreeNode != null) {

            const parentEntity = this.entityUtil.getActiveParentEntity(specificBranchRootEntity, entityTree, entityTreeData);
            const parentEntityData = this.entityUtil.getEntityDisplayData(parentEntity.name, entityTreeData);

            if (this.entityUtil.entityIsInitialized(parentEntity, parentEntityData)) {
                // Set hasInactive according to the currentTreeNode path state
                hasInactive = currentTreeNode.pathState != null
                        && currentTreeNode.pathState.inactivePath != null
                        && (currentTreeNode.pathState.inactivePath.isInPath
                                || currentTreeNode.pathState.inactivePath.isOrigin);
            }
        }

        return hasInactive;
    }

    /**
     * Returns if a given specific branch, associated with a selectedTab, has an unfinished path
     */
    treeTabHasUnfinishedPath(rootTreeNode: TreeNodeModel,
                             entityTree: EntityTreeModel,
                             entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>,
                             selectedTab: EntityTreeBranchTypeEnum): boolean {
        let hasUnfinishedPath = false;

        const selectedBranch = entityTree.specificBranches.get(selectedTab);

        // Get the 1st entity from the selected branch
        const specificBranchRootEntity: EntityTreeBranchModel = selectedBranch.values()
                .next().value;

        const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                specificBranchRootEntity, rootTreeNode, entityTree, entityTreeData);

        if (currentTreeNode != null) {

            const parentEntity = this.entityUtil.getActiveParentEntity(specificBranchRootEntity, entityTree, entityTreeData);
            const parentEntityData = this.entityUtil.getEntityDisplayData(parentEntity.name, entityTreeData);

            if (this.entityUtil.entityIsInitialized(parentEntity, parentEntityData)) {
                // First set hasUnfinishedPath according to the currentTreeNode path state
                hasUnfinishedPath = currentTreeNode.pathState != null
                        && currentTreeNode.pathState.unfinishedPath != null
                        && currentTreeNode.pathState.unfinishedPath.isInPath;

                // If there's a relation tree, override hasUnfinishedPath according to the relation tree node status
                if (this.treeNodeHasRelationTree(currentTreeNode)
                        && this.entityHasPathTo(parentEntity)) {

                    const filteredChildrenRelationTrees = this.selectedSpecificBranchFirstEntityRelationTrees(
                            currentTreeNode, entityTree, parentEntity, selectedTab);

                    if (filteredChildrenRelationTrees.length > 0) {
                        hasUnfinishedPath = this.treeNodeHasUnfinishedPath(filteredChildrenRelationTrees, entityTree, entityTreeData);
                    }
                }
            }
        }
        return hasUnfinishedPath;
    }

    /**
     * Returns the base state for a given tree node. If the node exists in the relation tree, returns the state directly.
     * Otherwise, calculates the path state according to the tree node state and tree configuration.
     */
    private getBaseTreeNodePathState(entity: EntityTreeBranchModel,
                                     rootTreeNode: TreeNodeModel,
                                     entityTree: EntityTreeModel,
                                     entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>):
            EntityElementPathStateModel {

        let state: EntityElementPathStateModel = null;

        const treeNode = this.entityUtil.getCurrentEntityTreeNode(entity, rootTreeNode, entityTree, entityTreeData);

        if (treeNode != null) {

            if ((treeNode.relationTree == null || treeNode.relationTree.length === 0)
                    || treeNode.relationTree.find((item) => item.type === entity.name) == null) {

                state = {
                    inactivePath: {
                        isOrigin: false,
                        isInPath: treeNode.pathState?.inactivePath?.isInPath || treeNode.pathState?.inactivePath?.isOrigin
                    },
                    unfinishedPath: {isOrigin: false, isInPath: true}
                };

            } else if (treeNode.relationTree.find((item) => item.type === entity.name) != null) {

                const code = this.getCodeToRetrieveTreeNodePathState(entity, treeNode, entityTreeData);
                if (code != null) {
                    state = treeNode.relationTree.find((entry) => entry.code === code).pathState;
                }
            }

        } else if (entity.isRoot) {
            const code = entity.path != null ? entity.path.code : null;

            if (code != null) {
                state = rootTreeNode.relationTree.find((entry) => entry.code === code).pathState;
            }
        }

        return state;
    }

    /**
     * Returns the tree node path state for an entity that has a child with the representParent attribute
     * @param entity the entity that is represented by the tree node
     * @param entityTree the corresponding entity tree configuration
     * @param initialState the base state for the entity
     */
    private getEntityWithRepresentableChildPathState(entity: EntityTreeBranchModel,
                                                     entityTree: EntityTreeModel,
                                                     entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>,
                                                     initialState: EntityElementPathStateModel): EntityElementPathStateModel {
        let state = initialState;
        const allChildrenAreMandatory = !entity.pathTo?.some((child) =>
                this.entityUtil.getEntityBranch(child.type, entityTree)
                        .get(child.type).skippable);

        const entityData = this.entityUtil.getEntityDisplayData(entity.name, entityTreeData);

        if (allChildrenAreMandatory && this.entityUtil.entityIsInitialized(entity, entityData)) {
            state = {...state, unfinishedPath: {isOrigin: false, isInPath: false}};
        }
        return state;
    }

    /**
     * Returns the tree node path state for an entity that has the representParent attribute
     * @param entity the entity that is represented by the tree node
     * @param rootTreeNode the relation tree to which the entity belongs
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding entity tree data
     * @param initialState the base state for the entity
     */
    private getRepresentableChildParentState(entity: EntityTreeBranchModel,
                                             rootTreeNode: TreeNodeModel,
                                             entityTree: EntityTreeModel,
                                             entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>,
                                             initialState: EntityElementPathStateModel): EntityElementPathStateModel {
        let parentPathState: EntityElementPathStateModel = null;
        const parent = this.entityUtil.getActiveParentEntity(entity, entityTree, entityTreeData);

        if (parent != null && parent.path != null && parent.path.code != null) {

            const parentNode = this.entityUtil.getCurrentEntityTreeNode(parent, rootTreeNode, entityTree, entityTreeData);
            if (parentNode != null && parentNode.relationTree != null) {

                const parentTreeNode = parentNode.relationTree
                        .find((entry) => entry.type === parent.name && parent.path.code === entry.code);

                if (parentTreeNode != null && parentTreeNode.relationTree != null) {

                    parentPathState = parentNode.relationTree.find((child) => child.code === parent.path.code).pathState;

                    const notRepresentableChild = parentTreeNode.relationTree.find(
                            (child) => !this.entityUtil.getEntityDisplayData(child.type, entityTreeData).representParent);
                    if (notRepresentableChild != null) {
                        const childIsInPathAndIsNotOrigin = notRepresentableChild.pathState.unfinishedPath.isInPath
                                && !notRepresentableChild.pathState.unfinishedPath.isOrigin;

                        parentPathState = {
                            ...parentPathState, unfinishedPath: {
                                isOrigin: childIsInPathAndIsNotOrigin,
                                isInPath: childIsInPathAndIsNotOrigin
                            }
                        };
                    }
                }
            }
        }
        return {...initialState, parentPathState: parentPathState};
    }

    /**
     * Returns the tree node path state for an entity that has the skippable attribute
     * @param entity the entity to check
     * @param rootTreeNode the relation tree to which the entity belongs
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding entity tree data
     */
    private getSkippableEntityPathState(entity: EntityTreeBranchModel,
                                        rootTreeNode: TreeNodeModel,
                                        entityTree: EntityTreeModel,
                                        entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>):
            EntityElementPathStateModel {

        let state: EntityElementPathStateModel = null;
        const parent = this.getActiveOrInactiveParentEntity(entity, entityTree, entityTreeData);

        if (parent != null) {
            const skippedEntityParentNode = this.entityUtil
                    .getCurrentEntityTreeNode(parent, rootTreeNode, entityTree, entityTreeData);

            if (skippedEntityParentNode != null && skippedEntityParentNode.relationTree != null
                    && parent.path != null && parent.path.code != null) {

                const skippedEntityParentPathState = skippedEntityParentNode.relationTree
                        .find((item) => item.code === parent.path.code);

                if (skippedEntityParentPathState != null) {
                    state = {
                        inactivePath: {
                            isOrigin: false,
                            isInPath: (skippedEntityParentPathState.pathState.inactivePath.isInPath
                                    || skippedEntityParentPathState.pathState.inactivePath.isOrigin)
                        },
                        unfinishedPath: {
                            isOrigin: false,
                            isInPath: skippedEntityParentPathState.pathState.unfinishedPath == null ?
                                    true : skippedEntityParentPathState.pathState.unfinishedPath.isInPath
                        }
                    };
                }
            }
        }

        return state;
    }

    /**
     * Returns the tree node path state for entities that have several children that are in different specific branches
     * @param entity the entity that is represented by the tree node
     * @param treeNode the relation tree node to which the entity belongs
     * @param entityTree the corresponding entity tree configuration
     * @param selectedBranch the selected specific branch from the entityTree
     * @param initialState the base state for the entity
     */
    private getEntityWithChildrenInDifferentBranchPathState(entity: EntityTreeBranchModel,
                                                            treeNode: TreeNodeModel,
                                                            entityTree: EntityTreeModel,
                                                            selectedBranch: EntityTreeBranchTypeEnum,
                                                            initialState: EntityElementPathStateModel): EntityElementPathStateModel {
        let state = initialState;

        if (entity.path != null) {

            const selectedBranchChildren = this.getEntityChildrenFromSpecificBranch(entity, entityTree, selectedBranch);

            if (selectedBranchChildren != null && selectedBranchChildren.length > 0) {
                const entityTreeNode = treeNode.relationTree.find((entry) => entity.name === entry.type
                        && entity.path.code === entry.code);

                if (entityTreeNode != null && entityTreeNode.relationTree != null) {
                    const hasAllChildrenFromBranch = selectedBranchChildren.every((item) => entityTreeNode.relationTree
                            .find((entry) => entry.type === item.type) != null);

                    state = {
                        ...state,
                        unfinishedPath: {
                            isOrigin: !hasAllChildrenFromBranch,
                            isInPath: !hasAllChildrenFromBranch
                        }
                    };
                }
            }
        }

        return state;
    }

    /**
     * Returns if a given entity is not present in a relation tree
     */
    private entityIsSkipped(entity: EntityTypeEnum, treeNode: TreeNodeModel, entityIsFinal: boolean, entityIsEnabled: boolean): boolean {
        let isSkipped = true;
        if (treeNode != null && treeNode.relationTree != null) {
            isSkipped = treeNode.relationTree.find((item) => item.type === entity) == null;
        }

        if (entityIsFinal && entityIsEnabled) {
            isSkipped = treeNode != null && treeNode.relationTree != null && treeNode.relationTree.length > 0;
        }

        return isSkipped;
    }

    /**
     * Returns an entity parent. If the entity has an active parent, return the active parent. Otherwise, return the 1st inactive parent.
     */
    private getActiveOrInactiveParentEntity(entity: EntityTreeBranchModel,
                                            entityTree: EntityTreeModel,
                                            entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>):
            EntityTreeBranchModel {
        let parent = this.entityUtil.getActiveParentEntity(entity, entityTree, entityTreeData);
        if (parent == null) {

            const entityPathFrom = entityTreeData.get(entity.name).pathFrom;

            if (entityPathFrom != null && entityPathFrom.length === 1) {
                parent = this.entityUtil.getEntityBranch(entityPathFrom[0].type, entityTree)
                        .get(entityPathFrom[0].type);
            }
        }

        return parent;
    }

    /**
     * Returns an entity pathToModel[] containing only the children that belong to a given branch
     */
    private getEntityChildrenFromSpecificBranch(entity: EntityTreeBranchModel,
                                                entityTree: EntityTreeModel,
                                                selectedBranch: EntityTreeBranchTypeEnum): PathToModel[] {
        let selectedBranchChildren: PathToModel[] = null;

        if (entity.pathTo != null) {
            selectedBranchChildren = entity.pathTo.filter((child) =>
                    entityTree.specificBranches.get(selectedBranch)
                            .get(child.type) != null);
        }

        return selectedBranchChildren;
    }

    /**
     * For a given {@link PathToModel[]} returns if any of the entries has the representParent value
     */
    private hasChildWithRepresentParent(pathTo: PathToModel[],
                                        entityTree: EntityTreeModel,
                                        entityDataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): boolean {
        let hasRepresentableChild = false;
        if (pathTo != null && entityTree != null) {
            hasRepresentableChild = pathTo.some((child) =>
                    this.entityUtil.getEntityDisplayData(child.type, entityDataTree).representParent);
        }
        return hasRepresentableChild;
    }

    /**
     * Returns the element code from which to retrieve the tree node path state.
     */
    private getCodeToRetrieveTreeNodePathState(entity: EntityTreeBranchModel,
                                               treeNode: TreeNodeModel,
                                               dataTree: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): string {
        let code = null;
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, dataTree);
        if (entityData.hasPath && entity.path != null) {
            code = entity.path.code;

        } else if (entityData.hasConnections && entity.connections != null && entity.connections.length > 0) {

            entity.connections.forEach((connection) => {
                const entryWithInactiveState = treeNode.relationTree.find((entry) => entry.code ===
                        connection.code && entry.pathState?.inactivePath != null && entry.pathState.inactivePath.isInPath);
                if (entryWithInactiveState != null) {
                    code = entryWithInactiveState.code;
                }
            });

        }
        return code;
    }

    /**
     * Returns if a given entity has at least one child that is not on the tree common branch
     */
    private entityHasChildInSpecificBranch(entity: EntityTreeBranchModel,
                                           entityTree: EntityTreeModel): boolean {
        let hasChildrenInSpecificBranch = false;

        if (entity.pathTo != null) {
            hasChildrenInSpecificBranch = entity.pathTo.some((child) => entityTree.commonBranch.get(child.type) == null);
        }

        return hasChildrenInSpecificBranch;
    }

    /**
     * Returns if a given entity is not in the common branch
     */
    private entityIsInCommonBranch(entity: EntityTreeBranchModel, entityTree: EntityTreeModel): boolean {
        return entityTree.commonBranch.get(entity.name) != null;
    }

    /**
     * Given an array of TreeNodeModels, recursively checks if there's and unfinished path
     */
    private treeNodeHasUnfinishedPath(childrenRelationTrees: TreeNodeModel[],
                                      entityTree: EntityTreeModel,
                                      entityTreeData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>): boolean {
        let unfinishedPath = childrenRelationTrees.some((entry) =>
                entry.pathState != null && entry.pathState.unfinishedPath != null && entry.pathState.unfinishedPath.isInPath);

        if (!unfinishedPath) {
            unfinishedPath = childrenRelationTrees.some((entry) => entry.relationTree != null ?
                    this.treeNodeHasUnfinishedPath(entry.relationTree, entityTree, entityTreeData)
                    : this.entityUtil.entityHasActiveChildren(entry.type, entityTreeData));
        }

        return unfinishedPath;
    }

    private treeNodeHasRelationTree(treeNode: TreeNodeModel): boolean {
        return treeNode.relationTree != null && treeNode.relationTree.length > 0;
    }

    private entityHasPathTo(entity: EntityTreeBranchModel): boolean {
        return entity.pathTo != null && entity.pathTo.length > 0;
    }

    private selectedSpecificBranchFirstEntityRelationTrees(treeNode: TreeNodeModel,
                                                           entityTree: EntityTreeModel,
                                                           parentEntity: EntityTreeBranchModel,
                                                           selectedTab: EntityTreeBranchTypeEnum): TreeNodeModel[] {
        let filteredChildrenRelationTrees = [];

        const specificBranchPath = this.getEntityChildrenFromSpecificBranch(parentEntity, entityTree, selectedTab);

        filteredChildrenRelationTrees = treeNode.relationTree.filter(
                (entry) => specificBranchPath.some((child) => child.type === entry.type));

        return filteredChildrenRelationTrees;
    }

}
